import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone, MessageCircle, Mail, Calendar, Stethoscope, Clock, Baby, Activity, Shield, Scale, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';
import { ToothIcon } from '../components/icons/ToothIcon';
import { ScalpelIcon } from '../components/icons/ScalpelIcon';

const appointmentTypes = [
  { icon: Stethoscope, title: 'Virtual Consultations', desc: 'Speak to a doctor from the comfort of your home via video call', color: 'bg-teal-100 text-teal-600' },
  { icon: ToothIcon, title: 'Dental Visits', desc: 'Cleanings, fillings, extractions, and all dental procedures', color: 'bg-blue-100 text-blue-600' },
  { icon: ScalpelIcon, title: 'Surgical Procedures', desc: 'Minor surgery, circumcisions, wart removal, and wound care', color: 'bg-purple-100 text-purple-600' },
  { icon: Calendar, title: 'Insurance & Medicals', desc: 'PrDP permits, fitness certificates, and insurance medicals', color: 'bg-amber-100 text-amber-600' },
];

const packageBookings = [
  {
    id: 'antenatal',
    icon: Baby,
    title: 'Antenatal Package',
    description: '4 antenatal visits with ultrasound scans',
    color: 'from-pink-500 to-rose-500',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-600',
  },
  {
    id: 'diabetic',
    icon: Activity,
    title: 'Diabetic Package',
    description: 'Comprehensive diabetic management program',
    color: 'from-blue-500 to-indigo-500',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
  },
  {
    id: 'hiv',
    icon: Shield,
    title: 'HIV Monitoring Package',
    description: 'Complete HIV care with blood tests included',
    color: 'from-violet-500 to-purple-500',
    bgColor: 'bg-violet-50',
    textColor: 'text-violet-600',
  },
];

export const Booking = () => {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  return (
    <div className="min-h-screen" data-testid="booking-page">
      <SEO
        title="Book an Appointment"
        description="Schedule your appointment at K&K Medical and Dental for virtual consultations, dental visits, surgical procedures, and insurance medicals."
        keywords="book appointment, virtual consultation, dental booking, Umzinto doctor, Scottburgh doctor"
      />

      <section className="relative bg-gradient-to-br from-slate-50 to-teal-50/30 py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-teal-100 text-teal-700 text-sm font-semibold mb-6">
            Appointments
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            Book Your Appointment
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-2">
            Online booking is available for the following services. For general walk-in consultations, no appointment is needed — simply visit us during operating hours.
          </p>
          <div className="flex items-center justify-center gap-2 text-slate-500 text-sm mt-4">
            <Clock className="w-4 h-4" />
            <span>Mon–Fri: 8AM–5PM | Sat: 8AM–2PM</span>
          </div>
        </div>
      </section>

      {/* Weight Loss & Wellness Booking — highlighted at top */}
      <section className="pt-12 pb-4 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card
            id="weight-loss"
            className="border-0 shadow-xl rounded-3xl overflow-hidden scroll-mt-32 relative"
            data-testid="booking-weight-loss"
          >
            {/* Featured badge */}
            <div className="absolute top-4 right-4 z-10 bg-white/95 backdrop-blur-sm text-rose-600 text-xs font-bold px-3 py-1.5 rounded-full shadow-md inline-flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" />
              New Programme
            </div>

            <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 p-5 sm:p-7 text-white">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="bg-white/20 rounded-xl p-2.5 sm:p-3 flex-shrink-0">
                  <Scale className="w-7 h-7 sm:w-9 sm:h-9" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold">Weight Loss &amp; Wellness</h3>
                  <p className="text-white/90 text-sm sm:text-base">
                    Doctor-led programme &mdash; screening, metabolic workup &amp; personalised plan
                  </p>
                </div>
              </div>
            </div>

            <CardContent className="p-5 sm:p-7">
              {/* Programme structure strip */}
              <div className="grid sm:grid-cols-3 gap-3 sm:gap-4 mb-5 sm:mb-6">
                <div className="rounded-2xl bg-teal-50 border border-teal-100 p-4 text-center">
                  <p className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-1">First Visit</p>
                  <p className="text-sm font-medium text-slate-700">Initial consultation &amp; full metabolic screen</p>
                </div>
                <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-center">
                  <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-1">Monthly</p>
                  <p className="text-sm font-medium text-slate-700">Ongoing monthly reviews &amp; support</p>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-rose-500 to-orange-500 p-4 text-center text-white shadow-md">
                  <p className="text-xs font-semibold uppercase tracking-widest mb-1">Annual Plan</p>
                  <p className="text-sm font-medium">12 months of reviews included — best value</p>
                </div>
              </div>

              <p className="text-slate-600 text-sm sm:text-base mb-5 sm:mb-6 leading-relaxed">
                To book your <span className="font-semibold">Weight Loss &amp; Wellness</span> consultation,
                contact us directly via WhatsApp or call. Our team will confirm your slot, brief you on the
                screening + metabolic tests involved in your first visit, and send you a personalised quote.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  asChild
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 text-white rounded-full px-5 sm:px-6 py-4 sm:py-5 shadow-lg w-full sm:w-auto"
                  data-testid="weight-loss-whatsapp-btn"
                >
                  <a
                    href="https://wa.me/27828951916?text=Hi%2C%20I%27d%20like%20to%20book%20a%20Weight%20Loss%20%26%20Wellness%20consultation."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Book via WhatsApp
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-slate-300 text-slate-700 hover:bg-slate-50 rounded-full px-5 sm:px-6 py-4 sm:py-5 w-full sm:w-auto"
                  data-testid="weight-loss-call-btn"
                >
                  <a href="tel:0399741570" className="inline-flex items-center justify-center gap-2">
                    <Phone className="w-5 h-5" />
                    Call 039 974 1570
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Package Booking Sections */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Healthcare Packages</h2>
            <p className="text-slate-600">Book your package appointment below</p>
          </div>

          <div className="space-y-6 mb-16">
            {packageBookings.map((pkg) => (
              <Card 
                key={pkg.id} 
                id={pkg.id} 
                className="border-0 shadow-lg rounded-2xl overflow-hidden scroll-mt-32"
                data-testid={`booking-${pkg.id}`}
              >
                <div className={`bg-gradient-to-r ${pkg.color} p-4 sm:p-6 text-white`}>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="bg-white/20 rounded-xl p-2 sm:p-3 flex-shrink-0">
                      <pkg.icon className="w-6 h-6 sm:w-8 sm:h-8" />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold">{pkg.title}</h3>
                      <p className="text-white/80 text-sm sm:text-base">{pkg.description}</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-4 sm:p-6">
                  <p className="text-slate-600 text-sm sm:text-base mb-4 sm:mb-6">
                    To book your <span className="font-semibold">{pkg.title}</span>, please contact us directly. 
                    Our team will guide you through the package details and schedule your first appointment.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <Button 
                      asChild 
                      className={`bg-gradient-to-r ${pkg.color} hover:opacity-90 text-white rounded-full px-5 sm:px-6 py-4 sm:py-5 shadow-lg w-full sm:w-auto`}
                      data-testid={`${pkg.id}-whatsapp-btn`}
                    >
                      <a href="https://wa.me/27828951916" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2">
                        <MessageCircle className="w-5 h-5" />
                        Book via WhatsApp
                      </a>
                    </Button>
                    <Button 
                      asChild 
                      variant="outline"
                      className="border-2 border-slate-300 text-slate-700 hover:bg-slate-50 rounded-full px-5 sm:px-6 py-4 sm:py-5 w-full sm:w-auto"
                      data-testid={`${pkg.id}-call-btn`}
                    >
                      <a href="tel:0399741570" className="inline-flex items-center justify-center gap-2">
                        <Phone className="w-5 h-5" />
                        Call 039 974 1570
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Appointment Types */}
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Other Services</h2>
            <p className="text-slate-600">Book online for these appointment types</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            {appointmentTypes.map((type) => (
              <Card key={type.title} className="border-0 shadow-lg rounded-2xl" data-testid={`type-${type.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-6 flex items-start gap-4">
                  <div className={`p-3 rounded-xl shrink-0 ${type.color}`}>
                    <type.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-lg mb-1">{type.title}</h3>
                    <p className="text-slate-600 text-sm">{type.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-3xl p-8 lg:p-12 text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ready to Book?</h2>
            <p className="text-teal-100 mb-8 max-w-xl mx-auto">Contact us directly to schedule your visit.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="bg-white text-teal-700 hover:bg-slate-100 rounded-full px-8 py-6 text-lg shadow-lg" data-testid="book-whatsapp-btn">
                <a href="https://wa.me/27828951916" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Book via WhatsApp
                </a>
              </Button>
              <Button asChild variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-teal-700 rounded-full px-8 py-6 text-lg" data-testid="book-call-btn">
                <a href="tel:0399741570" className="inline-flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Umzinto: 039 974 1570
                </a>
              </Button>
              <Button asChild variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-teal-700 rounded-full px-8 py-6 text-lg" data-testid="book-call-scottburgh-btn">
                <a href="tel:0393155041" className="inline-flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Scottburgh: 039 315 5041
                </a>
              </Button>
            </div>
          </div>

          <div className="bg-slate-50 rounded-2xl p-6 sm:p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">Prefer to Contact Us Directly?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <a href="tel:0399741570" className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow" data-testid="contact-phone">
                <div className="p-2 bg-teal-100 rounded-lg flex-shrink-0"><Phone className="w-5 h-5 text-teal-600" /></div>
                <div>
                  <p className="text-xs text-slate-500">Umzinto</p>
                  <p className="font-semibold text-slate-900">039 974 1570</p>
                </div>
              </a>
              <a href="tel:0393155041" className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow" data-testid="contact-phone-scottburgh">
                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0"><Phone className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-xs text-slate-500">Scottburgh</p>
                  <p className="font-semibold text-slate-900">039 315 5041</p>
                </div>
              </a>
              <a href="https://wa.me/27828951916" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow" data-testid="contact-whatsapp">
                <div className="p-2 bg-green-100 rounded-lg flex-shrink-0"><MessageCircle className="w-5 h-5 text-green-600" /></div>
                <div>
                  <p className="text-xs text-slate-500">WhatsApp</p>
                  <p className="font-semibold text-slate-900">082 895 1916</p>
                </div>
              </a>
              <a href="mailto:info@kkmedical.co.za" className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow" data-testid="contact-email">
                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0"><Mail className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="font-semibold text-slate-900 text-sm sm:text-base">info@kkmedical.co.za</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
